import React, { useState, useEffect } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Layers, MapPin, ShieldAlert, FileDown, Loader2, Mail, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const C = { navy: '#0B1E3D', blue: '#1A4C7C', slate: '#5C6B7A', green: '#1D9E75', amber: '#E8A020', red: '#E24B4A', orange: '#FF9933' };
const FLAG = { ok: C.green, watch: C.amber, breach: C.red, unmeasured: C.slate };
const fmtM = (v) => (v >= 1e9 ? `$${(v / 1e9).toFixed(2)}B` : v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : `$${Math.round(v / 1e3)}K`);

function ShareList({ title, icon: Icon, rows, testid }) {
  const max = Math.max(...rows.map(r => r.gwp), 1);
  return (
    <Card className="p-4 rounded-sm" style={{ border: '1px solid rgba(136,150,168,0.14)' }} data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4" style={{ color: C.blue }} />
        <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>{title}</h3>
      </div>
      <div className="space-y-2">
        {rows.map(r => (
          <div key={r.label}>
            <div className="flex items-center justify-between text-[11px] mb-0.5">
              <span className="font-medium truncate pr-2" style={{ color: C.navy }}>{r.label}</span>
              <span className="font-mono shrink-0" style={{ color: C.slate }}>{fmtM(r.gwp)} · {r.gwp_share_pct}%</span>
            </div>
            <div className="h-1.5 rounded-full" style={{ background: '#EDF1F5' }}>
              <div className="h-1.5 rounded-full" style={{ width: `${(r.gwp / max) * 100}%`, background: C.blue }} />
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function ConcentrationPage() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [sizing, setSizing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailNote, setEmailNote] = useState('Please find attached our one-page Capacity Pack ahead of our treaty discussion — happy to walk through the accumulation model and proposed structure at your convenience.');
  const [sending, setSending] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactName, setContactName] = useState('');
  const [contactCompany, setContactCompany] = useState('');

  const loadContacts = () => api.get('/analytics/reinsurer-contacts').then(r => setContacts(r.data.contacts)).catch(() => {});

  const openEmail = () => { setEmailOpen(true); loadContacts(); };

  const deleteContact = async (c) => {
    try {
      await api.delete(`/analytics/reinsurer-contacts/${c.contact_id}`);
      setContacts(prev => prev.filter(x => x.contact_id !== c.contact_id));
      toast.info(`Removed ${c.name || c.email} from the contact book`);
    } catch { toast.error('Could not remove contact'); }
  };

  const toggleAutoSend = async (c) => {
    const enabled = !c.auto_send;
    try {
      await api.post(`/analytics/reinsurer-contacts/${c.contact_id}/auto-send`, { enabled });
      setContacts(prev => prev.map(x => x.contact_id === c.contact_id ? { ...x, auto_send: enabled } : x));
      toast.success(enabled
        ? `${c.name || c.email} will get the refreshed pack automatically at each quarter end`
        : `Quarterly auto-send off for ${c.name || c.email}`);
    } catch { toast.error('Could not update auto-send'); }
  };

  const knownContact = contacts.some(c => c.email.toLowerCase() === emailTo.trim().toLowerCase());

  const sendPack = async () => {
    setSending(true);
    try {
      const r = await api.post('/analytics/capacity-pack/email', {
        to: emailTo.trim(), note: emailNote,
        contact_name: contactName, contact_company: contactCompany,
      });
      toast.success(`Capacity Pack emailed to ${r.data.to}`);
      setEmailOpen(false);
      setEmailTo(''); setContactName(''); setContactCompany('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send the Capacity Pack');
    } finally { setSending(false); }
  };

  const downloadPack = async () => {
    setDownloading(true);
    try {
      const res = await api.get('/analytics/capacity-pack.pdf', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `fraime-capacity-pack-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Capacity Pack downloaded — one page, reinsurer-ready');
    } catch {
      toast.error('Could not generate the Capacity Pack');
    } finally { setDownloading(false); }
  };

  useEffect(() => {
    Promise.all([
      api.get('/analytics/concentration'),
      api.get('/analytics/treaty-rightsizing').catch(() => ({ data: null })),
    ]).then(([r, s]) => { setData(r.data); setSizing(s.data); })
      .catch(e => console.error(e?.message)).finally(() => setLoading(false));
  }, [api]);

  if (loading) return <div className="space-y-3">{[1, 2, 3].map(i => <Skeleton key={i} className="h-24 w-full" />)}</div>;
  if (!data) return <p className="text-sm" style={{ color: C.slate }}>Concentration data unavailable.</p>;

  const breaches = data.by_postcode.filter(p => p.flag === 'watch' || p.flag === 'breach');
  const measured = data.treaty.limit_configured;
  return (
    <div className="space-y-4" data-testid="concentration-page">
      <div className="flex flex-wrap items-center gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ color: C.navy, fontFamily: 'Georgia, serif' }}>Concentration</h1>
          <p className="text-sm mt-1" style={{ color: C.slate }}>
            {data.total.policies.toLocaleString()} in-force · GWP {fmtM(data.total.gwp)} · exposure {fmtM(data.total.sum_insured)} ·
            {measured
              ? ` aggregate limit ${fmtM(data.treaty.aggregate_limit_per_postcode)} per postcode`
              : ` single-event XoL cover ${fmtM(data.treaty.single_event_cover)} — no aggregate limit set`}
          </p>
        </div>
        {measured && breaches.length > 0 && (
          <span className="ml-auto text-[11px] font-semibold px-2.5 py-1 rounded-sm" data-testid="concentration-flag-count"
            style={{ background: 'rgba(226,75,74,0.1)', color: C.red }}>
            {breaches.length} postcode{breaches.length === 1 ? '' : 's'} at or over the referral line
          </span>
        )}
        {!measured && (
          <span className="ml-auto text-[11px] font-semibold px-2.5 py-1 rounded-sm" data-testid="concentration-unmeasured"
            style={{ background: 'rgba(232,160,32,0.12)', color: '#B07407' }}>
            Unmeasured — set the aggregate limit at Portfolio → Capacity Gate
          </span>
        )}
        <button onClick={downloadPack} disabled={downloading} data-testid="capacity-pack-download"
          className={`${breaches.length > 0 ? '' : 'ml-auto '}flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold text-white disabled:opacity-50 transition-opacity hover:opacity-90`}
          style={{ background: C.navy }}>
          {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
          Capacity Pack PDF
        </button>
        <button onClick={openEmail} data-testid="capacity-pack-email-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold transition-colors hover:bg-slate-50"
          style={{ color: C.navy, border: '1px solid rgba(136,150,168,0.35)', background: '#FFF' }}>
          <Mail className="w-3.5 h-3.5" />
          Email to reinsurer
        </button>
        <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
          <DialogContent className="sm:max-w-md" data-testid="capacity-pack-email-dialog">
            <DialogHeader>
              <DialogTitle style={{ color: C.navy }}>Email Capacity Pack</DialogTitle>
              <DialogDescription className="text-xs">
                Sends the one-page PDF with a covering note straight to your reinsurer contact.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              {contacts.length > 0 && (
                <div data-testid="reinsurer-contact-book">
                  <label className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: C.slate }}>Saved contacts</label>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {contacts.map(c => (
                      <span key={c.contact_id} data-testid={`reinsurer-contact-${c.contact_id}`}
                        className="group flex items-center gap-1 pl-2 pr-1 py-1 rounded-sm text-[11px] cursor-pointer transition-colors"
                        style={{ background: emailTo.trim().toLowerCase() === c.email.toLowerCase() ? 'rgba(26,76,124,0.12)' : 'rgba(136,150,168,0.1)', border: '1px solid rgba(136,150,168,0.25)', color: C.navy }}
                        onClick={() => setEmailTo(c.email)}
                        title={c.last_sent_at ? `Last sent ${new Date(c.last_sent_at).toLocaleDateString('en-AU')}` : ''}>
                        <span className="font-semibold">{c.name || c.email}</span>
                        {c.company && <span style={{ color: C.slate }}>· {c.company}</span>}
                        <button onClick={(e) => { e.stopPropagation(); toggleAutoSend(c); }}
                          data-testid={`reinsurer-contact-autosend-${c.contact_id}`}
                          title={c.auto_send ? 'Quarterly auto-send ON — click to turn off' : 'Turn on quarterly auto-send'}
                          className="ml-0.5 px-1 rounded-sm transition-opacity"
                          style={{ opacity: c.auto_send ? 1 : 0.35 }}>
                          <CalendarClock className="w-3 h-3" style={{ color: c.auto_send ? C.green : C.slate }} />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); deleteContact(c); }}
                          data-testid={`reinsurer-contact-delete-${c.contact_id}`}
                          className="ml-0.5 px-1 rounded-sm opacity-40 hover:opacity-100 transition-opacity"
                          style={{ color: C.red }}>×</button>
                      </span>
                    ))}
                  </div>
                  <p className="text-[10px] mt-1.5" style={{ color: C.slate }} data-testid="autosend-footnote">
                    {contacts.filter(c => c.auto_send).length > 0
                      ? `${contacts.filter(c => c.auto_send).length} contact${contacts.filter(c => c.auto_send).length === 1 ? '' : 's'} will receive the refreshed pack automatically at each quarter end.`
                      : 'Tip: click the calendar icon on a contact to auto-send the refreshed pack every quarter end.'}
                  </p>
                </div>
              )}
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: C.slate }}>Recipient email</label>
                <input type="email" value={emailTo} onChange={e => setEmailTo(e.target.value)}
                  placeholder="treaty@reinsurer.com" data-testid="capacity-pack-email-to"
                  className="mt-1 w-full px-3 py-2 text-sm rounded-sm border focus:outline-none"
                  style={{ borderColor: 'rgba(136,150,168,0.35)', color: C.navy }} />
              </div>
              {emailTo.trim() && !knownContact && (
                <div className="flex gap-2" data-testid="reinsurer-contact-new-fields">
                  <div className="flex-1">
                    <label className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: C.slate }}>Contact name (saved for reuse)</label>
                    <input value={contactName} onChange={e => setContactName(e.target.value)}
                      placeholder="e.g. Sarah Ng" data-testid="reinsurer-contact-name"
                      className="mt-1 w-full px-3 py-2 text-xs rounded-sm border focus:outline-none"
                      style={{ borderColor: 'rgba(136,150,168,0.35)', color: C.navy }} />
                  </div>
                  <div className="flex-1">
                    <label className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: C.slate }}>Company</label>
                    <input value={contactCompany} onChange={e => setContactCompany(e.target.value)}
                      placeholder="e.g. Swiss Re" data-testid="reinsurer-contact-company"
                      className="mt-1 w-full px-3 py-2 text-xs rounded-sm border focus:outline-none"
                      style={{ borderColor: 'rgba(136,150,168,0.35)', color: C.navy }} />
                  </div>
                </div>
              )}
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: C.slate }}>Covering note</label>
                <textarea rows={4} value={emailNote} onChange={e => setEmailNote(e.target.value)}
                  data-testid="capacity-pack-email-note"
                  className="mt-1 w-full px-3 py-2 text-xs rounded-sm border focus:outline-none resize-none"
                  style={{ borderColor: 'rgba(136,150,168,0.35)', color: C.navy }} />
              </div>
              <button onClick={sendPack} disabled={sending || !emailTo.trim()} data-testid="capacity-pack-email-send"
                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-sm text-sm font-semibold text-white disabled:opacity-50 transition-opacity hover:opacity-90"
                style={{ background: C.navy }}>
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                {sending ? 'Sending…' : 'Send Capacity Pack'}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ShareList title="GWP by Class" icon={Layers} rows={data.by_class.slice(0, 10)} testid="concentration-by-class" />
        <ShareList title="GWP by State" icon={MapPin} rows={data.by_state.slice(0, 9)} testid="concentration-by-state" />
      </div>

      <Card className="p-4 rounded-sm" style={{ border: '1px solid rgba(136,150,168,0.14)' }} data-testid="concentration-by-postcode">
        <div className="flex items-center gap-2 mb-1">
          <ShieldAlert className="w-4 h-4" style={{ color: C.orange }} />
          <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>
            {measured ? 'Top Postcode Accumulations vs the Aggregate Limit' : 'Top Postcode Accumulations'}
          </h3>
        </div>
        <p className="text-[10px] mb-3" style={{ color: C.slate }}>
          {measured
            ? `PML at ${Math.round(data.treaty.pml_damage_ratio * 100)}% assumed event damage ratio vs the ${fmtM(data.treaty.aggregate_limit_per_postcode)} per-postcode aggregate limit. The bind door refers at ${data.treaty.refer_pct}% and refuses at ${data.treaty.refuse_pct}% of the same number.`
            : `PML at ${Math.round(data.treaty.pml_damage_ratio * 100)}% assumed event damage ratio, shown as a multiple of the ${fmtM(data.treaty.single_event_cover)} single-event XoL cover. That is exposure context, not a treaty breach — an aggregate limit has not been entered.`}
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead><tr className="border-b" style={{ borderColor: 'rgba(136,150,168,0.2)' }}>
              {['Postcode', 'Policies', 'GWP', 'Sum insured', 'PML', measured ? 'Utilisation of limit' : '× single-event cover', ''].map(h => (
                <th key={h} className="py-1.5 pr-4 text-[9px] uppercase tracking-wider font-bold" style={{ color: C.slate }}>{h}</th>))}
            </tr></thead>
            <tbody>
              {data.by_postcode.map(p => (
                <tr key={p.label} className="border-b" style={{ borderColor: 'rgba(136,150,168,0.08)' }} data-testid={`postcode-row-${p.label}`}>
                  <td className="py-1.5 pr-4 text-xs font-mono font-semibold" style={{ color: C.navy }}>{p.label}</td>
                  <td className="py-1.5 pr-4 text-xs font-mono" style={{ color: C.slate }}>{p.policies}</td>
                  <td className="py-1.5 pr-4 text-xs font-mono" style={{ color: C.slate }}>{fmtM(p.gwp)}</td>
                  <td className="py-1.5 pr-4 text-xs font-mono" style={{ color: C.navy }}>{fmtM(p.sum_insured)}</td>
                  <td className="py-1.5 pr-4 text-xs font-mono" style={{ color: C.navy }}>{fmtM(p.pml)}</td>
                  <td className="py-1.5 pr-4 w-40">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 rounded-full" style={{ background: '#EDF1F5' }}>
                        <div className="h-1.5 rounded-full" style={{ width: `${Math.min(p.treaty_utilisation_pct ?? Math.min((p.pml_multiple_of_event_cover || 0) * 20, 100), 100)}%`, background: FLAG[p.flag] }} />
                      </div>
                      <span className="text-[10px] font-mono whitespace-nowrap" style={{ color: FLAG[p.flag] }}>
                        {p.treaty_utilisation_pct == null
                          ? `${p.pml_multiple_of_event_cover}×`
                          : p.treaty_utilisation_pct >= 200 ? `${(p.treaty_utilisation_pct / 100).toFixed(1)}× limit` : `${p.treaty_utilisation_pct}%`}
                      </span>
                    </div>
                  </td>
                  <td className="py-1.5">
                    <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm" style={{ background: `${FLAG[p.flag]}1A`, color: FLAG[p.flag] }}>{p.flag}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {sizing && <RightSizingCard s={sizing} />}
    </div>
  );
}

function RightSizingCard({ s }) {
  const allLayers = [...(s.current.layers || []).map(l => ({ ...l, existing: true })),
                     ...(s.proposed_layers || [])];
  return (
    <Card className="p-4 rounded-sm" style={{ border: '1px solid rgba(26,76,124,0.25)' }} data-testid="treaty-rightsizing-card">
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <ShieldAlert className="w-4 h-4" style={{ color: C.blue }} />
        <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Treaty Right-Sizing</h3>
        <span className="ml-auto text-[11px] font-semibold px-2.5 py-1 rounded-sm" data-testid="rightsizing-gap"
          style={{ background: s.current.shortfall > 0 ? 'rgba(226,75,74,0.1)' : 'rgba(29,158,117,0.1)', color: s.current.shortfall > 0 ? C.red : C.green }}>
          {s.current.shortfall > 0 ? `Capacity gap: ${fmtM(s.current.shortfall)}` : 'Program adequate'}
        </span>
      </div>
      <p className="text-[11px] mb-3" style={{ color: C.slate }}>
        Driver: postcode <strong>{s.driver.postcode}</strong> PML {fmtM(s.driver.pml)} (at {Math.round(s.driver.pml_damage_ratio * 100)}% damage ratio)
        vs current capacity {fmtM(s.current.capacity)} → required program ≈ <strong style={{ color: C.navy }}>{fmtM(s.required_capacity)}</strong>
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead><tr className="border-b" style={{ borderColor: 'rgba(136,150,168,0.2)' }}>
            {['Layer', 'Attachment', 'Limit', 'Rate on line', 'Est. premium', ''].map(h => (
              <th key={h} className="py-1.5 pr-4 text-[9px] uppercase tracking-wider font-bold" style={{ color: C.slate }}>{h}</th>))}
          </tr></thead>
          <tbody>
            {allLayers.map(l => (
              <tr key={l.layer_id} className="border-b" style={{ borderColor: 'rgba(136,150,168,0.08)' }} data-testid={`rightsizing-layer-${l.layer_id}`}>
                <td className="py-1.5 pr-4 text-xs font-medium" style={{ color: C.navy }}>{l.name}</td>
                <td className="py-1.5 pr-4 text-xs font-mono" style={{ color: C.slate }}>{fmtM(l.attachment)}</td>
                <td className="py-1.5 pr-4 text-xs font-mono" style={{ color: C.navy }}>{fmtM(l.limit)}</td>
                <td className="py-1.5 pr-4 text-xs font-mono" style={{ color: C.slate }}>{l.premium_rate_pct}%</td>
                <td className="py-1.5 pr-4 text-xs font-mono" style={{ color: C.navy }}>{fmtM(l.premium ?? (l.limit * l.premium_rate_pct / 100))}</td>
                <td className="py-1.5">
                  <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm"
                    style={{ background: l.existing ? 'rgba(136,150,168,0.12)' : 'rgba(26,76,124,0.1)', color: l.existing ? C.slate : C.blue }}>
                    {l.existing ? 'In force' : 'Proposed'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex flex-wrap gap-4 text-[11px]" style={{ color: C.slate }}>
        <span>Cost of closing the gap: <strong className="font-mono" style={{ color: C.red }} data-testid="rightsizing-cost">{fmtM(s.additional_premium)}/yr</strong></span>
        <span>= <strong className="font-mono" style={{ color: C.navy }}>{s.additional_premium_pct_of_gwp}%</strong> of active GWP</span>
        <span>Total program premium: <strong className="font-mono" style={{ color: C.navy }}>{fmtM(s.total_program_premium)}</strong></span>
      </div>
      <p className="text-[10px] mt-2" style={{ color: C.slate }}>{s.note}</p>
    </Card>
  );
}
